import type {
  ArcDef,
  CastDef,
  CoverSpec,
  EndingDef,
  IssueCastDef,
  IssueStoryDef,
  LocationRuleDef,
  StoryCheckpointDef,
  StoryIssueRulesDef,
  StoryLoadoutDef,
  StoryRewardsDef,
} from './types';

/*
 * The C# initializers of shared-cs Content/StoryDefs.cs, so a draft without a field shows (and validates as) what the server's parser
 * fills in. Every list starts empty and the feature is off (`storyRules.enabled`).
 */

export const storyLoadoutDefaults = (): Required<StoryLoadoutDef> => ({
  weapon: '',
  weapon2: '',
  armor: '',
  items: [],
});

export const castDefaults = (id = ''): Required<CastDef> => ({
  id,
  name: '',
  pronouns: 'they',
  lookSeed: 0,
  wear: [],
  npcId: '',
  maxHealth: 100,
  loadout: storyLoadoutDefaults(),
  bio: '',
  appearance: null,
});

export const endingDefaults = (id = ''): Required<EndingDef> => ({
  id,
  title: '',
  when: null,
  beatId: '',
  cosmeticId: '',
});

export const arcDefaults = (id = ''): Required<ArcDef> => ({
  id,
  title: '',
  kind: 'oneoff',
  blurb: '',
  logo: { text: '', color: '', sprite: '' },
  visibility: 'listed',
  status: 'coming_soon',
  unlock: { afterIssue: '', triggerId: '' },
  issues: [],
  cast: [],
  endings: [],
  continues: false,
  teasers: [],
  order: 0,
  devOnly: false,
});

export const locationRuleDefaults = (): Required<LocationRuleDef> => ({
  kind: 'place',
  kinds: [],
  categories: [],
  minAreaM2: 0,
  minRooms: 0,
  issue: '',
  fallbackKm: 0,
  pickNear: 'home',
  signsFlag: '',
});

export const coverDefaults = (): Required<CoverSpec> => ({
  panel: null,
  title: '',
  tagline: '',
  kicker: '',
  variants: [],
});

export const storyRewardsDefaults = (): Required<StoryRewardsDef> => ({
  perRun: [],
  firstRead: '',
  onLocation: [],
  kit: [],
});

export const issueCastDefaults = (castId = ''): Required<IssueCastDef> => ({
  castId,
  when: null,
  essential: false,
  loadout: null,
  protectedUntil: '',
});

export const checkpointDefaults = (id = ''): Required<StoryCheckpointDef> => ({
  id,
  objective: '',
  stage: '',
  label: '',
});

export const issueStoryDefaults = (arcId = ''): Required<IssueStoryDef> => ({
  arcId,
  number: 0,
  kicker: '',
  minutes: 0,
  heroIsPlayer: false,
  castLead: '',
  cast: [],
  loadout: storyLoadoutDefaults(),
  location: locationRuleDefaults(),
  cover: coverDefaults(),
  intro: [],
  outro: [],
  checkpoints: [],
  rewards: storyRewardsDefaults(),
});

export const storyIssueRulesDefaults = (): Required<StoryIssueRulesDef> => ({
  enabled: false,
  playAnywhere: false,
  ticketHours: 3,
  fallbackSearchKm: 3,
  storyDropKm: 1,
  storyDropDays: 7,
  sameAsFallbackKm: 5,
  pickedWithinKm: 3,
  signsHours: 48,
  shortCutsceneAfter: 1,
});

/** A new arc for the collection editor's Add: a coming-soon one-off with no issues. */
export const newArc = (id: string): ArcDef => ({ ...arcDefaults(id), title: 'New arc' });

/** The Step 4 sections with C# defaults (merged into `teamsSectionDefaults`), shown for a draft without them. */
export const s4SectionDefaults = {
  storyRules: storyIssueRulesDefaults,
} as const;
